import { track } from "@opensea/analytics"
import { Image, Link } from "@opensea/next-ui-kit"
import type { LinkProps } from "@opensea/ui-kit"
import {
  createConfig,
  CoreWalletProvider as WalletProviderBase,
  type WalletProviderProps as WalletProviderBaseProps,
} from "@opensea/wallet"
import { captureException } from "@sentry/nextjs"
import { useState } from "react"
import { type CreateConnectorFn, WagmiProvider } from "wagmi"
import { useReferralParam } from "@/hooks/use-referral-param"
import { toWalletCurrencies } from "@/lib/currencies"
import { trackError as trackErrorDatadog } from "../../lib/integrations/datadog"
import type { Chain, Currency } from "../ClientProviders/client-providers-query"
import { WalletAnalyticsHandler } from "./analytics"
import { getConnectors } from "./connectors"
import { FlagContextHandler } from "./flags"
import { useWalletFlags } from "./flags/use-wallet-flags"
import { siweAdapter } from "./siwe-adapter"

type WalletProviderProps = {
  chains: Chain[]
  children: React.ReactNode
  initialCurrencies: Currency[]
  connectors?: CreateConnectorFn[]
}

export function WalletProvider({
  children,
  chains,
  initialCurrencies,
  connectors,
}: WalletProviderProps) {
  const evmChains = chains.filter(chain => chain.arch === "EVM")

  const [wagmiConfig] = useState(() => {
    // Sorted so that mainnet is first
    const sortedEvmChains = evmChains?.sort((a, b) => a.networkId - b.networkId)
    return createConfig({
      chains: sortedEvmChains,
      connectors: connectors ?? getConnectors(),
      ssr: true,
    })
  })

  const flags = useWalletFlags()
  const [flagsReady, setFlagsReady] = useState(false)

  useReferralParam()

  return (
    <WagmiProvider config={wagmiConfig}>
      <WalletProviderBase
        acceptTermsEnabled
        chains={chains}
        flags={flags}
        flagsReady={flagsReady}
        initialCurrencies={toWalletCurrencies(initialCurrencies)}
        renderImage={renderImage}
        renderLink={renderLink}
        siweAdapter={siweAdapter}
        trackError={trackError}
        trackEvent={track}
        wagmiConfig={wagmiConfig}
      >
        {children}
        <FlagContextHandler setReady={setFlagsReady} />
        <WalletAnalyticsHandler />
      </WalletProviderBase>
    </WagmiProvider>
  )
}

const renderLink = ({ href, ...restProps }: LinkProps) => {
  if (!href) {
    throw new Error("Link component requires an href prop")
  }
  return <Link href={href} {...restProps} />
}

const renderImage: WalletProviderBaseProps["renderImage"] = props => {
  return (
    <Image
      height={props.height ?? props.size}
      width={props.width ?? props.size}
      {...props}
    />
  )
}

const trackError: WalletProviderBaseProps["trackError"] = (error, context) => {
  console.error(error, context)

  // Only send to Sentry if not explicitly skipped
  let errorId: string | undefined
  if (!context?.extra?.skipSentry) {
    errorId = captureException(error, context)
  }

  void trackErrorDatadog(error, context)

  // Still track in Amplitude regardless
  if (context?.extra?.type === "blockchain-action-error") {
    void track("Blockchain Action Error", {
      error: error.message,
      errorName: error.name,
      errorId,
      cause: (error.cause as Error | undefined)?.message,
      ...context,
    })
  }
  return errorId
}
